import { WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper, PDFManagerFactory } from 'pdftron';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import {
  DocumentTypes,
  PDFTronTools,
  PDFTRON_DEFAULT_TOOL,
  Difference,
  AnnotationCustomData,
  DifferenceTypeNames,
} from 'types';
import ToolsMixin from '../Tools';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import quadsToDimensions from '../utils/quadsToDimensions';
import { Core } from '@pdftron/webviewer';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import store from 'store/store';
import {
  inspection,
  getInspectionId,
  app,
  getDifferenceViewOptions,
  getFilteredAndUnfilteredDifferences,
  getLastJobInput,
} from 'store';
import { resyncAnnotations, fastResyncExcludeDifferences } from 'store/request/inspections/actions';
import { getDifferences } from 'store/request/differences/selectors';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import isResultUrl from 'utils/location/isResultUrl';
import { isRectangularCrop, isPolygonCrop } from './utils/CropToolUtils';
import { pointInPolygon } from './utils/PolygonToolUtils';

// While RESYNC tool is active, block spacebar hotkey so Pan doesn't engage and trigger switchOut
let __spacebarBlockHandler: ((ev: KeyboardEvent) => void) | undefined;
function toggleSpacebarHotkey(enable: boolean) {
  if (enable) {
    if (__spacebarBlockHandler) return;
    const spaceHandler = (ev: KeyboardEvent) => {
      const key = (ev as any).code || (ev as any).key;
      if (key === 'Space' || key === ' ') {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    __spacebarBlockHandler = spaceHandler;
    const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
    const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
    sourceWin?.addEventListener('keydown', spaceHandler, { capture: true } as any);
    if (targetWin && targetWin !== sourceWin) {
      targetWin.addEventListener('keydown', spaceHandler, { capture: true } as any);
    }
  } else {
    if (!__spacebarBlockHandler) return;
    const sourceWin = PDFTronManager.getIframeWindow(DocumentTypes.source);
    const targetWin = PDFTronManager.getIframeWindow(DocumentTypes.target);
    sourceWin?.removeEventListener('keydown', __spacebarBlockHandler, { capture: true } as any);
    if (targetWin && targetWin !== sourceWin) {
      targetWin.removeEventListener('keydown', __spacebarBlockHandler, { capture: true } as any);
    }
    __spacebarBlockHandler = undefined;
  }
}

// Difference types allowed for resync operations
export const RESYNC_ALLOWED_TYPES: ReadonlySet<DifferenceTypeNames> = new Set([
  DifferenceTypeNames.Change,
  DifferenceTypeNames.Insertion,
  DifferenceTypeNames.Deletion,
] as DifferenceTypeNames[]);

// Minimum fraction of a text quad's area that must overlap the resync zone
// Used for both live preview and finalized fragment computation
const RESYNC_ZONE_MIN_OVERLAP_RATIO = 0.5;

const RESYNC_ZONE_CONFIG = {
  // When merging adjacent rects, allowed vertical misalignment and horizontal gap
  mergeYAlignmentTolerance: 0.75,
  mergeXGapTolerance: 0.75,
  // When avoiding crossing out text that is covered by a highlight, how much of the text
  // must be inside the highlight in both width and height
  highlightOverlapThreshold: 0.95,
};

export interface SerializedResyncZoneAnnotation {
  pageNumber: number;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * Helper function to extract bounds from a quad
 * @param quad A quad object with x1, y1, x2, y2, x3, y3, x4, y4 properties
 * @returns Object with left, right, top, bottom bounds
 */
function getQuadBounds(quad: any): { left: number; right: number; top: number; bottom: number } {
  return {
    left: Math.min(quad.x1, quad.x2, quad.x3, quad.x4),
    right: Math.max(quad.x1, quad.x2, quad.x3, quad.x4),
    top: Math.min(quad.y1, quad.y2, quad.y3, quad.y4),
    bottom: Math.max(quad.y1, quad.y2, quad.y3, quad.y4),
  };
}

/**
 * Helper function to check if a point is inside a rectangle
 */
function isPointInRectangle(px: number, py: number, left: number, top: number, right: number, bottom: number): boolean {
  return px >= left && px <= right && py >= top && py <= bottom;
}

/**
 * Helper function to check if two line segments intersect
 */
function doLineSegmentsIntersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
): boolean {
  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  if (denom === 0) return false; // Parallel lines

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

/**
 * Helper function to check if a rectangle intersects with a polygon
 * Checks all three cases: rect corners in polygon, polygon corners in rect, and edge crossings
 */
function doesRectangleIntersectPolygon(
  rectLeft: number,
  rectTop: number,
  rectRight: number,
  rectBottom: number,
  polygonPoints: number[][],
): boolean {
  // Case 1: Any corner of rectangle inside polygon
  const rectCorners = [
    { x: rectLeft, y: rectTop },
    { x: rectRight, y: rectTop },
    { x: rectRight, y: rectBottom },
    { x: rectLeft, y: rectBottom },
  ];

  if (
    rectCorners.some((corner) =>
      pointInPolygon(
        corner,
        polygonPoints.map(([x, y]) => ({ x, y })),
      ),
    )
  ) {
    return true;
  }

  // Case 2: Any corner of polygon inside rectangle
  if (polygonPoints.some((point) => isPointInRectangle(point[0], point[1], rectLeft, rectTop, rectRight, rectBottom))) {
    return true;
  }

  // Case 3: Any edge of polygon intersects any edge of rectangle
  const rectEdges = [
    { x1: rectLeft, y1: rectTop, x2: rectRight, y2: rectTop }, // Top edge
    { x1: rectRight, y1: rectTop, x2: rectRight, y2: rectBottom }, // Right edge
    { x1: rectRight, y1: rectBottom, x2: rectLeft, y2: rectBottom }, // Bottom edge
    { x1: rectLeft, y1: rectBottom, x2: rectLeft, y2: rectTop }, // Left edge
  ];

  for (let i = 0; i < polygonPoints.length; i++) {
    const j = (i + 1) % polygonPoints.length;
    const polyEdge = {
      x1: polygonPoints[i][0],
      y1: polygonPoints[i][1],
      x2: polygonPoints[j][0],
      y2: polygonPoints[j][1],
    };

    for (const rectEdge of rectEdges) {
      if (
        doLineSegmentsIntersect(
          polyEdge.x1,
          polyEdge.y1,
          polyEdge.x2,
          polyEdge.y2,
          rectEdge.x1,
          rectEdge.y1,
          rectEdge.x2,
          rectEdge.y2,
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

class ResyncZone {
  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get toolsMixin() {
    return new ToolsMixin(this.source, this.target);
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  // Helper method to get inspected annotations
  private getUserPrepMarkups(pageNumber: number, documentType: DocumentTypes) {
    const crossouts: Array<{ quads: any[]; bounds: { x: number; y: number; width: number; height: number } }> = [];
    const cropZones: Array<{ x: number; y: number; width: number; height: number; points?: number[][] }> = [];
    const textZones: Array<{ x: number; y: number; width: number; height: number }> = [];

    const lastJobInput = getLastJobInput(store.getState());
    if (lastJobInput) {
      const documentTypes = {
        [DocumentTypes.source]: 'master',
        [DocumentTypes.target]: 'sample',
      };

      lastJobInput.documents?.forEach((inputDocument) => {
        if (documentTypes[documentType] === inputDocument.type) {
          inputDocument.pages?.forEach((inputPage) => {
            if (inputPage.number !== pageNumber) return;

            // Get crossouts
            inputPage.crossout?.forEach((crossout) => {
              const dims = quadsToDimensions(crossout.quads);
              crossouts.push({
                quads: crossout.quads,
                bounds: { x: dims.X, y: dims.Y, width: dims.Width, height: dims.Height },
              });
            });

            // Get crop zones
            inputPage.crop?.forEach((crop) => {
              if (isRectangularCrop(crop)) {
                cropZones.push({ x: crop.x1, y: crop.y1, width: crop.x2 - crop.x1, height: crop.y2 - crop.y1 });
              } else if (isPolygonCrop(crop)) {
                const points = crop.points || [];
                // Calculate bounding box for polygon
                if (points.length > 0) {
                  const xs = points.map((p) => p[0]);
                  const ys = points.map((p) => p[1]);
                  const minX = Math.min(...xs);
                  const maxX = Math.max(...xs);
                  const minY = Math.min(...ys);
                  const maxY = Math.max(...ys);
                  cropZones.push({ x: minX, y: minY, width: maxX - minX, height: maxY - minY, points });
                }
              }
            });

            // Get text zones
            inputPage.zone?.forEach((zone) => {
              const dims = quadsToDimensions(zone.quads || []);
              textZones.push({ x: dims.X, y: dims.Y, width: dims.Width, height: dims.Height });
            });
          });
        }
      });
    }

    return { crossouts, cropZones, textZones };
  }

  createResyncZoneAnnotation(instance: WebViewerInstance) {
    const outerThis = this;
    class ResyncZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        this.setCustomData('resyncZoneAnnotation', 'true');

        this.ToolName = PDFTronTools.RESYNC;
        this.IsHoverable = false;
        this.disableRotationControl();
        this.NoResize = true;
        this.NoMove = true;
        this.ReadOnly = true;
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);
        ctx.strokeStyle = '#02A4B6';
        ctx.stroke(annotationRectangle);

        // While drawing, draw temporary blue text highlight in the zone unless disabled bc a diff box is in the zone
        if (this.getCustomData('resyncDisableBlueTextHighlights') !== 'true') {
          const pageNumber = this.PageNumber;
          const zoneLeft = this.X;
          const zoneRight = this.X + this.Width;
          const zoneTop = this.Y;
          const zoneBottom = this.Y + this.Height;

          const isSource = PDFManagerFactory.getViewer(DocumentTypes.source)?.instance === instance;
          const documentType = isSource ? DocumentTypes.source : DocumentTypes.target;

          // Get inspected annotations (crossouts, crop zones, text zones) on this page
          const inspectedAnnots = outerThis.getUserPrepMarkups(pageNumber, documentType);
          const hasInspectedZones = inspectedAnnots.cropZones.length > 0 || inspectedAnnots.textZones.length > 0;

          ctx.save();

          // Create a clipping path using evenodd fill rule
          const clipPath = new Path2D();

          // 1. Add the resync zone rectangle
          clipPath.rect(zoneLeft, zoneTop, this.Width, this.Height);

          // 2. If there are crop/text zones, add full page rect then add back valid zones (evenodd)
          if (hasInspectedZones) {
            const pageDoc = instance.Core.documentViewer.getDocument();
            if (pageDoc) {
              const pageInfo = pageDoc.getPageInfo(pageNumber);
              if (pageInfo) {
                // Add outer rectangle (entire page) - this will subtract everything with evenodd
                clipPath.rect(0, 0, pageInfo.width, pageInfo.height);

                // Add back the valid crop zones
                inspectedAnnots.cropZones.forEach((zone) => {
                  if (zone.points && zone.points.length >= 3) {
                    // Polygon zone
                    clipPath.moveTo(zone.points[0][0], zone.points[0][1]);
                    for (let i = 1; i < zone.points.length; i++) {
                      clipPath.lineTo(zone.points[i][0], zone.points[i][1]);
                    }
                    clipPath.closePath();
                  } else {
                    // Rectangular zone
                    clipPath.rect(zone.x, zone.y, zone.width, zone.height);
                  }
                });

                // Add back the valid text zones (always rectangular)
                // Skip text zones that are fully contained within crop zones to avoid evenodd flipping
                inspectedAnnots.textZones.forEach((textZone) => {
                  let isFullyWithinCropZone = false;
                  for (const cropZone of inspectedAnnots.cropZones) {
                    if (cropZone.points && cropZone.points.length >= 3) {
                      // Check if all 4 corners of text zone are within polygon crop zone
                      const corners = [
                        { x: textZone.x, y: textZone.y },
                        { x: textZone.x + textZone.width, y: textZone.y },
                        { x: textZone.x + textZone.width, y: textZone.y + textZone.height },
                        { x: textZone.x, y: textZone.y + textZone.height },
                      ];
                      if (
                        corners.every((corner) =>
                          pointInPolygon(
                            corner,
                            cropZone.points!.map(([x, y]) => ({ x, y })),
                          ),
                        )
                      ) {
                        isFullyWithinCropZone = true;
                        break;
                      }
                    } else {
                      // Check if text zone is fully within rectangular crop zone
                      if (
                        textZone.x >= cropZone.x &&
                        textZone.y >= cropZone.y &&
                        textZone.x + textZone.width <= cropZone.x + cropZone.width &&
                        textZone.y + textZone.height <= cropZone.y + cropZone.height
                      ) {
                        isFullyWithinCropZone = true;
                        break;
                      }
                    }
                  }
                  // Only add text zone to clip path if it's not fully contained in a crop zone
                  if (!isFullyWithinCropZone) {
                    clipPath.rect(textZone.x, textZone.y, textZone.width, textZone.height);
                  }
                });
              }
            }
          }

          // 3. Add crossout rectangles (these will be subtracted via evenodd)
          inspectedAnnots.crossouts.forEach((crossout) => {
            if (crossout.quads && Array.isArray(crossout.quads)) {
              crossout.quads.forEach((quad: any) => {
                const { left, right, top, bottom } = getQuadBounds(quad);
                const width = right - left;
                const height = bottom - top;
                if (width > 0 && height > 0) {
                  clipPath.rect(left, top, width, height);
                }
              });
            }
          });

          // Apply the clip path with evenodd fill rule
          ctx.clip(clipPath, 'evenodd');
          ctx.fillStyle = 'rgba(0, 228, 245, 0.25)';

          // Use character-level quads cached in custom data (set by mouseMove handler)
          const cachedQuads = this.getCustomData('livePreviewQuads');
          const MIN_OVERLAP_RATIO = RESYNC_ZONE_MIN_OVERLAP_RATIO;

          if (cachedQuads) {
            try {
              const textPositions = JSON.parse(cachedQuads);

              // Batch all rectangles into a single Path2D to prevent alpha accumulation on overlaps
              const highlightPath = new Path2D();

              for (let i = 0; i < textPositions.length; i++) {
                const q = textPositions[i];
                const left = Math.min(q.x1, q.x2, q.x3, q.x4);
                const right = Math.max(q.x1, q.x2, q.x3, q.x4);
                const top = Math.min(q.y1, q.y2, q.y3, q.y4);
                const bottom = Math.max(q.y1, q.y2, q.y3, q.y4);
                const width = right - left;
                const height = bottom - top;
                if (width <= 0 || height <= 0) continue;

                // Quick reject if outside zone bbox
                if (right < zoneLeft || left > zoneRight || bottom < zoneTop || top > zoneBottom) continue;

                // Center-in-zone rule: ensures text is actually inside the resync zone
                const cx = (left + right) / 2;
                const cy = (top + bottom) / 2;
                const centerInside = cx >= zoneLeft && cx <= zoneRight && cy >= zoneTop && cy <= zoneBottom;
                if (!centerInside) continue;

                // Overlap threshold rule: ensures sufficient overlap (avoids barely-touching text)
                const overlapLeft = Math.max(left, zoneLeft);
                const overlapRight = Math.min(right, zoneRight);
                const overlapTop = Math.max(top, zoneTop);
                const overlapBottom = Math.min(bottom, zoneBottom);
                const overlapW = Math.max(0, overlapRight - overlapLeft);
                const overlapH = Math.max(0, overlapBottom - overlapTop);
                const overlapArea = overlapW * overlapH;
                const quadArea = width * height;
                const overlapRatio = quadArea > 0 ? overlapArea / quadArea : 0;
                if (overlapRatio < MIN_OVERLAP_RATIO) continue;

                // Check if crossed out (character-level check, same as final)
                let isCrossedOut = false;
                for (const crossout of inspectedAnnots.crossouts) {
                  if (crossout.quads && Array.isArray(crossout.quads)) {
                    for (const crossoutQuad of crossout.quads) {
                      const { left: qLeft, right: qRight, top: qTop, bottom: qBottom } = getQuadBounds(crossoutQuad);
                      // Check if center of character is crossed out
                      if (cx >= qLeft && cx <= qRight && cy >= qTop && cy <= qBottom) {
                        isCrossedOut = true;
                        break;
                      }
                    }
                    if (isCrossedOut) break;
                  }
                }
                if (isCrossedOut) continue;

                // Add rectangle to path (batched drawing prevents alpha accumulation)
                highlightPath.rect(left, top, width, height);
              }

              // Draw all highlights in one operation for uniform opacity
              ctx.fill(highlightPath);
            } catch (e) {
              // Fall back to no preview if cached data is invalid
            }
          }

          ctx.restore();
        }
      }
    }
    return ResyncZoneAnnotation;
  }

  private createResyncTextHighlightAnnotation(instance: WebViewerInstance) {
    class ResyncZoneTextHighlightAnnotation extends GVAnnotationMixin(
      instance.Core.Annotations.TextHighlightAnnotation,
    ) {
      constructor() {
        super();
        this.ReadOnly = true;
        this.NoResize = true;
        this.NoMove = true;
        this.IsHoverable = false;
        this.setCustomData('resyncBlueTextHighlight', 'true');
      }

      draw(ctx: CanvasRenderingContext2D, _pageMatrix: any, _pageRotation: number, _zoomLevel: number) {
        const data = this.getCustomData('resyncZoneBlueTextHighlight');
        if (!data) return;
        const fragments = JSON.parse(data);
        if (!Array.isArray(fragments) || fragments.length === 0) return;
        ctx.save();
        ctx.fillStyle = 'rgba(0, 228, 245, 0.25)';
        ctx.globalCompositeOperation = 'destination-over';
        fragments.forEach((frag: any) => {
          const rect = frag.rect;
          if (!rect || rect.length !== 4) return;
          const [x, y, width, height] = rect;
          if (width > 0 && height > 0) {
            ctx.fillRect(x, y, width, height);
          }
        });
        ctx.restore();
      }
    }
    return ResyncZoneTextHighlightAnnotation;
  }

  private async computeTextFragmentsForZone(
    wrapper: WebViewerWrapper,
    zone: Core.Annotations.Annotation,
  ): Promise<Array<{ pageNumber: number; rect: [number, number, number, number] }>> {
    try {
      const { instance } = wrapper;
      const pageNumber = zone.PageNumber;

      const zoneLeft = zone.X;
      const zoneRight = zone.X + zone.Width;
      const zoneTop = zone.Y;
      const zoneBottom = zone.Y + zone.Height;
      const MIN_OVERLAP_RATIO = RESYNC_ZONE_MIN_OVERLAP_RATIO; // match live selection behavior

      const isSource = PDFManagerFactory.getViewer(DocumentTypes.source)?.instance === instance;
      const documentType = isSource ? DocumentTypes.source : DocumentTypes.target;

      // Get inspected annotations from both annotation manager and Redux store
      const inspectedAnnots = this.getUserPrepMarkups(pageNumber, documentType);
      const hasInspectedZones = inspectedAnnots.cropZones.length > 0 || inspectedAnnots.textZones.length > 0;

      // Helper function to check if a text quad is inside any crop/text zone
      const isQuadInValidZone = (left: number, top: number, right: number, bottom: number): boolean => {
        if (!hasInspectedZones) return true; // No zones means entire page is valid

        // Check center point of the quad
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;

        // Check crop zones (might be rectangular or polygon)
        for (const zone of inspectedAnnots.cropZones) {
          if (zone.points && zone.points.length >= 3) {
            // Polygon crop zone
            if (
              pointInPolygon(
                { x: cx, y: cy },
                zone.points.map(([x, y]) => ({ x, y })),
              )
            )
              return true;
          } else {
            // Rectangular crop zone
            if (cx >= zone.x && cx <= zone.x + zone.width && cy >= zone.y && cy <= zone.y + zone.height) {
              return true;
            }
          }
        }

        // Check text zones (always rectangular)
        for (const zone of inspectedAnnots.textZones) {
          if (cx >= zone.x && cx <= zone.x + zone.width && cy >= zone.y && cy <= zone.y + zone.height) {
            return true;
          }
        }

        return false;
      };

      // Helper function to check if a text quad overlaps with any crossout
      const isQuadCrossedOut = (left: number, top: number, right: number, bottom: number): boolean => {
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;

        for (const crossout of inspectedAnnots.crossouts) {
          if (crossout.quads && Array.isArray(crossout.quads)) {
            for (const quad of crossout.quads) {
              const { left: qLeft, right: qRight, top: qTop, bottom: qBottom } = getQuadBounds(quad);

              // Check if center of text quad is inside crossout quad
              if (cx >= qLeft && cx <= qRight && cy >= qTop && cy <= qBottom) {
                return true;
              }
            }
          }
        }
        return false;
      };

      const doc = instance.Core.documentViewer.getDocument();
      const pageText = await doc.loadPageText(pageNumber);
      const textPositions = (await doc.getTextPosition(pageNumber, 0, pageText.length)) as Core.Math.Quad[];
      if (!textPositions || textPositions.length === 0) return [];

      // 1) Collect candidate rects from character quads using same rules + inspected annotations filtering
      const candidates: Array<{ left: number; top: number; right: number; bottom: number }> = [];
      for (let i = 0; i < textPositions.length; i++) {
        const q = textPositions[i];
        const left = Math.min(q.x1, q.x2, q.x3, q.x4);
        const right = Math.max(q.x1, q.x2, q.x3, q.x4);
        const top = Math.min(q.y1, q.y2, q.y3, q.y4);
        const bottom = Math.max(q.y1, q.y2, q.y3, q.y4);
        const width = right - left;
        const height = bottom - top;
        if (width <= 0 || height <= 0) continue;

        // Quick reject if outside zone bbox
        if (right < zoneLeft || left > zoneRight || bottom < zoneTop || top > zoneBottom) continue;

        // Center-in-zone rule
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;
        if (!(cx >= zoneLeft && cx <= zoneRight && cy >= zoneTop && cy <= zoneBottom)) continue;

        // Overlap threshold rule
        const overlapLeft = Math.max(left, zoneLeft);
        const overlapRight = Math.min(right, zoneRight);
        const overlapTop = Math.max(top, zoneTop);
        const overlapBottom = Math.min(bottom, zoneBottom);
        const overlapW = Math.max(0, overlapRight - overlapLeft);
        const overlapH = Math.max(0, overlapBottom - overlapTop);
        const overlapArea = overlapW * overlapH;
        const quadArea = width * height;
        const overlapRatio = quadArea > 0 ? overlapArea / quadArea : 0;
        if (overlapRatio < MIN_OVERLAP_RATIO) continue;

        // Skip if quad is crossed out
        if (isQuadCrossedOut(left, top, right, bottom)) continue;

        // Skip if quad is outside crop/text zones (when they exist)
        if (!isQuadInValidZone(left, top, right, bottom)) continue;

        candidates.push({ left, top, right, bottom });
      }

      if (candidates.length === 0) return [];

      // 2) Merge adjacent rects on same line to reduce fragment count
      const yTol = RESYNC_ZONE_CONFIG.mergeYAlignmentTolerance; // line alignment tolerance
      const xGapTol = RESYNC_ZONE_CONFIG.mergeXGapTolerance; // acceptable small gaps between glyphs
      candidates.sort((a, b) => (a.top === b.top ? a.left - b.left : a.top - b.top));
      const merged: Array<{ left: number; top: number; right: number; bottom: number }> = [];
      let cur = candidates[0];
      for (let i = 1; i < candidates.length; i++) {
        const nxt = candidates[i];
        const sameLine = Math.abs(nxt.top - cur.top) <= yTol && Math.abs(nxt.bottom - cur.bottom) <= yTol;
        const touchesOrOverlaps = nxt.left <= cur.right + xGapTol;
        if (sameLine && touchesOrOverlaps) {
          // extend current box
          cur.right = Math.max(cur.right, nxt.right);
          cur.top = Math.min(cur.top, nxt.top);
          cur.bottom = Math.max(cur.bottom, nxt.bottom);
        } else {
          merged.push(cur);
          cur = nxt;
        }
      }
      merged.push(cur);

      // 3) Convert to final fragment format
      const fragments: Array<{ pageNumber: number; rect: [number, number, number, number] }> = merged.map((m) => ({
        pageNumber,
        rect: [m.left, m.top, Math.max(0, m.right - m.left), Math.max(0, m.bottom - m.top)],
      }));

      return fragments;
    } catch (e) {
      return [];
    }
  }

  createResyncZoneAnnotationTool() {
    if (this.source && this.target) {
      [this.source, this.target].forEach((wrapper: WebViewerWrapper) => {
        const { instance } = wrapper;
        const self = this;
        const toolObject = new (class ResyncZoneCreateTool extends instance.Core.Tools.RectangleCreateTool {
          constructor(resyncZoneAnnotationTool: any) {
            super(instance.Core.documentViewer);
            instance.Core.Tools.GenericAnnotationCreateTool.call(
              this,
              instance.Core.documentViewer,
              resyncZoneAnnotationTool,
            );
          }

          switchIn(previousTool: any) {
            super.switchIn(previousTool);
            this.cursor = 'crosshair';
            toggleSpacebarHotkey(true);

            // When manual tool is active, add a class to the viewer to force crosshair
            // and disable annotation hover/interaction via CSS
            const viewerElement = instance.Core.documentViewer.getScrollViewElement();
            if (viewerElement) {
              viewerElement.classList.add('manual-tool-mode');
            }

            self.hideDisallowedDifferences(); // Hide differences that are not allowed for resync while the tool is active
          }

          switchOut(newTool: any) {
            super.switchOut(newTool);
            const viewerElement = instance.Core.documentViewer.getScrollViewElement();
            if (viewerElement) {
              viewerElement.classList.remove('manual-tool-mode');
            }

            toggleSpacebarHotkey(false);

            // Remove resync-zone preview text highlight annotations when leaving the tool
            const annotMgr = instance.Core.annotationManager;
            const resyncTextHighlights = annotMgr
              .getAnnotationsList()
              .filter((a: Core.Annotations.Annotation) => a.getCustomData?.('resyncBlueTextHighlight') === 'true');
            if (resyncTextHighlights.length)
              annotMgr.deleteAnnotations(resyncTextHighlights, { imported: true, force: true });
          }
        })(this.createResyncZoneAnnotation(instance));

        // Hook mouse events to toggle live text highlighting
        const defaultMouseMove = (toolObject as any).mouseMove?.bind(toolObject);
        (toolObject as any).mouseMove = (e: any) => {
          if (defaultMouseMove) defaultMouseMove(e);
          const annot = toolObject.annotation as Core.Annotations.Annotation | undefined;
          if (!annot) return;
          // Live toggle difference boxes to blue as they enter the zone
          const documentType = wrapper === self.source ? DocumentTypes.source : DocumentTypes.target;
          self.applyBlueDifferenceBoxesToSelectedDiffs(documentType, annot);

          // Load and cache character-level quads for accurate live preview
          if (!annot.getCustomData('livePreviewQuads')) {
            const pageNumber = annot.PageNumber;
            const doc = instance.Core.documentViewer.getDocument();
            doc
              .loadPageText(pageNumber)
              .then((pageText) => {
                return doc.getTextPosition(pageNumber, 0, pageText.length);
              })
              .then((textPositions: any) => {
                if (textPositions && textPositions.length > 0) {
                  annot.setCustomData('livePreviewQuads', JSON.stringify(textPositions));
                  instance.Core.annotationManager.redrawAnnotation(annot);
                }
              })
              .catch(() => {
                // Silently fail - zone will just show without text preview
              });
          }

          // As soon as the zone touches/intersects a difference box, disable live text highlighting for this zone
          const annotsIntersecting = self.getAnnotationsWithinResyncZone(documentType, annot);
          if (annotsIntersecting && annotsIntersecting.length > 0) {
            if (annot.getCustomData('resyncDisableBlueTextHighlights') !== 'true') {
              annot.setCustomData('resyncDisableBlueTextHighlights', 'true');
              instance.Core.annotationManager.redrawAnnotation(annot);
            }
          } else if (annot.getCustomData('resyncDisableBlueTextHighlights') === 'true') {
            // If no boxes are currently intersecting, re-enable live text highlighting
            annot.setCustomData('resyncDisableBlueTextHighlights', '');
            instance.Core.annotationManager.redrawAnnotation(annot);
          }
        };

        wrapper.registerTool({
          toolName: PDFTronTools.RESYNC,
          toolObject,
          buttonImage: '',
        });
      });
    }
  }

  resyncZoneAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    // Only handle ADD action (when annotation is created)
    if (action !== AnnotationAction.ADD || !annotation.getCustomData('resyncZoneAnnotation')) {
      return;
    }
    const currentWrapper = documentType === DocumentTypes.source ? this.source : this.target;
    const otherWrapper = documentType === DocumentTypes.source ? this.target : this.source;

    // Switch tool to PAN so we cannot draw another zone
    currentWrapper?.instance.UI.setToolMode(PDFTronTools.PAN);

    if (currentWrapper) {
      // Check if zone is completely outside crop/text zones (if such zones exist)
      const prepMarkups = this.getUserPrepMarkups(annotation.PageNumber, documentType);
      const hasCropOrTextZones = prepMarkups.cropZones.length > 0 || prepMarkups.textZones.length > 0;

      if (hasCropOrTextZones) {
        // Check if zone overlaps with any crop/text zone
        let overlapsAnyZone = false;
        const zoneLeft = annotation.X;
        const zoneRight = annotation.X + annotation.Width;
        const zoneTop = annotation.Y;
        const zoneBottom = annotation.Y + annotation.Height;

        // Check crop zones
        for (const zone of prepMarkups.cropZones) {
          if (zone.points && zone.points.length >= 3) {
            // Polygon crop zone - complete intersection check
            if (doesRectangleIntersectPolygon(zoneLeft, zoneTop, zoneRight, zoneBottom, zone.points)) {
              overlapsAnyZone = true;
              break;
            }
          } else {
            // Rectangular crop zone - simple bounding box check
            const zoneOverlaps = !(
              zoneLeft >= zone.x + zone.width ||
              zoneRight <= zone.x ||
              zoneTop >= zone.y + zone.height ||
              zoneBottom <= zone.y
            );
            if (zoneOverlaps) {
              overlapsAnyZone = true;
              break;
            }
          }
        }

        // Check text zones if not already overlapping
        if (!overlapsAnyZone) {
          for (const zone of prepMarkups.textZones) {
            const zoneOverlaps = !(
              zoneLeft >= zone.x + zone.width ||
              zoneRight <= zone.x ||
              zoneTop >= zone.y + zone.height ||
              zoneBottom <= zone.y
            );
            if (zoneOverlaps) {
              overlapsAnyZone = true;
              break;
            }
          }
        }

        // If zone doesn't overlap any crop/text zone, reject it
        if (!overlapsAnyZone) {
          const annotMgr = currentWrapper.instance.Core.annotationManager;
          annotMgr.deleteAnnotations([annotation], { imported: true, force: true });
          currentWrapper.instance.UI.setToolMode(PDFTronTools.RESYNC);
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'This content was not included in the original inspection',
              type: 'error',
            }),
          );
          return;
        }
      }

      // Hide the resync zone (the blue rectangle, selections will still be visible with blue highlights)
      const annotMgr = currentWrapper.instance.Core.annotationManager;
      annotation.Hidden = true;
      annotation.NoView = true;
      annotMgr.redrawAnnotation(annotation);

      // FLOW 1: No selected difference boxes, so we create Text Highlight annotations for the zone
      const annotsIntersecting = this.getAnnotationsWithinResyncZone(documentType, annotation);
      if (!annotsIntersecting || annotsIntersecting.length === 0) {
        this.computeTextFragmentsForZone(currentWrapper, annotation)
          .then((fragments) => {
            // If no text highlights to draw, delete the resync zone (do not accept it) and re-enable resync tool on the doc
            if (fragments.length === 0) {
              // Check if the zone contains crossouts - if so, that's likely why there are no fragments
              let hasCrossoutsInZone = false;
              if (prepMarkups.crossouts.length > 0) {
                const zoneLeft = annotation.X;
                const zoneRight = annotation.X + annotation.Width;
                const zoneTop = annotation.Y;
                const zoneBottom = annotation.Y + annotation.Height;

                hasCrossoutsInZone = prepMarkups.crossouts.some((crossout) => {
                  return crossout.quads.some((quad: any) => {
                    const { left, right, top, bottom } = getQuadBounds(quad);
                    // Check if crossout quad intersects with zone
                    return !(left > zoneRight || right < zoneLeft || top > zoneBottom || bottom < zoneTop);
                  });
                });
              }

              annotMgr.deleteAnnotations([annotation], { imported: true, force: true });
              currentWrapper.instance.UI.setToolMode(PDFTronTools.RESYNC);

              // Show warning if crossouts were found in the zone
              if (hasCrossoutsInZone) {
                store.dispatch(
                  app.actions.setSnackMessage({
                    message: 'This content was not included in the original inspection',
                    type: 'error',
                  }),
                );
              }
              return;
            }
            // Otherwise create the more permanent blue text highlight annotations
            const HighlightClass = this.createResyncTextHighlightAnnotation(currentWrapper.instance);
            const highlight = new HighlightClass();
            highlight.PageNumber = annotation.PageNumber;
            highlight.setCustomData('resyncZoneBlueTextHighlight', JSON.stringify(fragments));
            highlight.Id = `${annotation.Id}-resync-zone-highlight`;
            const rect = new currentWrapper.instance.Core.Math.Rect(
              annotation.X,
              annotation.Y,
              annotation.X + annotation.Width,
              annotation.Y + annotation.Height,
            );
            highlight.setRect(rect);
            annotMgr.addAnnotation(highlight);
            annotMgr.redrawAnnotation(highlight);

            // Auto-optimize the resync zone to encompass the drawn text highlights (union of fragment rects)
            let minX = Number.POSITIVE_INFINITY;
            let minY = Number.POSITIVE_INFINITY;
            let maxX = Number.NEGATIVE_INFINITY;
            let maxY = Number.NEGATIVE_INFINITY;
            fragments.forEach((frag) => {
              const r = frag?.rect;
              if (!r || r.length !== 4) return;
              const x1 = r[0];
              const y1 = r[1];
              const x2 = r[0] + r[2];
              const y2 = r[1] + r[3];
              if (x1 < minX) minX = x1;
              if (y1 < minY) minY = y1;
              if (x2 > maxX) maxX = x2;
              if (y2 > maxY) maxY = y2;
            });
            if (isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY) && maxX > minX && maxY > minY) {
              const optimized = new currentWrapper.instance.Core.Math.Rect(minX, minY, maxX, maxY);
              annotation.setRect(optimized);
              annotation.setCustomData('optimizedResyncZone', 'true');
              annotMgr.redrawAnnotation(annotation);
            }
          })
          .catch(() => undefined);
      }
    }

    // FLOW 2: Zone contains difference boxes, so we optimize the zone to encompass them all
    if (annotation.getCustomData('optimizedResyncZone') !== 'true') {
      const includedBoxes = this.getAnnotationsWithinResyncZone(documentType, annotation);
      if (includedBoxes && includedBoxes.length > 0 && currentWrapper) {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        includedBoxes.forEach((a) => {
          const left = a.X;
          const top = a.Y;
          const right = a.X + a.Width;
          const bottom = a.Y + a.Height;
          if (left < minX) minX = left;
          if (top < minY) minY = top;
          if (right > maxX) maxX = right;
          if (bottom > maxY) maxY = bottom;
        });

        if (isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY) && maxX > minX && maxY > minY) {
          const optimized = new currentWrapper.instance.Core.Math.Rect(minX, minY, maxX, maxY);
          annotation.setRect(optimized);
          annotation.setCustomData('optimizedResyncZone', 'true');
          currentWrapper.instance.Core.annotationManager.redrawAnnotation(annotation);
        }
      }
    }

    // Check if the other document also has a resync zone
    if (otherWrapper) {
      const otherAnnotations = otherWrapper.getAnnotationList();
      const hasResyncZone = otherAnnotations.some(
        (annot: Core.Annotations.Annotation) => annot.getCustomData('resyncZoneAnnotation') === 'true',
      );

      // Other doc does not have a resync zone
      if (!hasResyncZone) {
        this.applyBlueDifferenceBoxesToSelectedDiffs(documentType, annotation);
        if (currentWrapper) {
          // Clear any previous resync crossouts on this document
          this.deleteResyncCrossoutsFromDocument(currentWrapper);
        }
        if (documentType === DocumentTypes.source) {
          // first zone drawn on source → highlight target
          this.source?.removeAnnotationDocumentHighlight();
          this.target?.addDocumentHighlight();
        } else {
          // first zone drawn on target → keep highlight on source
          this.target?.removeAnnotationDocumentHighlight();
          this.source?.addDocumentHighlight();
        }

        otherWrapper.instance.UI.setToolMode(PDFTronTools.RESYNC);
      } else {
        // Both documents have resync zones
        const sourceZones = this.getResyncZones(DocumentTypes.source);
        const targetZones = this.getResyncZones(DocumentTypes.target);
        if (sourceZones.length && targetZones.length) {
          // Compute included text on both sides; if equal and non-empty → fast resync, else full resync
          const sourceZone = sourceZones[0];
          const targetZone = targetZones[0];
          const sourceAnnots = this.getAnnotationsWithinResyncZone(DocumentTypes.source, sourceZone);
          const targetAnnots = this.getAnnotationsWithinResyncZone(DocumentTypes.target, targetZone);

          Promise.all([
            this.getIncludedTextForResync(this.source, sourceAnnots, sourceZone),
            this.getIncludedTextForResync(this.target, targetAnnots, targetZone),
          ])
            .then(([sourceIncludedText, targetIncludedText]) => {
              const s = (sourceIncludedText || '').trim();
              const t = (targetIncludedText || '').trim();

              // Check if there are any prep crossouts in either resync zone
              let hasPrepCrossouts = false;
              if (this.source) {
                const sourcePrepAnnots = this.getUserPrepMarkups(sourceZone.PageNumber, DocumentTypes.source);
                if (sourcePrepAnnots.crossouts.length > 0) {
                  // Check if any prep crossouts intersect with the source resync zone
                  hasPrepCrossouts = sourcePrepAnnots.crossouts.some((crossout) => {
                    return crossout.quads.some((quad: any) => {
                      const { left, right, top, bottom } = getQuadBounds(quad);
                      const zoneBounds = {
                        left: sourceZone.X,
                        right: sourceZone.X + sourceZone.Width,
                        top: sourceZone.Y,
                        bottom: sourceZone.Y + sourceZone.Height,
                      };
                      return !(
                        left > zoneBounds.right ||
                        right < zoneBounds.left ||
                        top > zoneBounds.bottom ||
                        bottom < zoneBounds.top
                      );
                    });
                  });
                }
              }

              if (!hasPrepCrossouts && this.target) {
                const targetPrepAnnots = this.getUserPrepMarkups(targetZone.PageNumber, DocumentTypes.target);
                if (targetPrepAnnots.crossouts.length > 0) {
                  // Check if any prep crossouts intersect with the target resync zone
                  hasPrepCrossouts = targetPrepAnnots.crossouts.some((crossout) => {
                    return crossout.quads.some((quad: any) => {
                      const { left, right, top, bottom } = getQuadBounds(quad);
                      const zoneBounds = {
                        left: targetZone.X,
                        right: targetZone.X + targetZone.Width,
                        top: targetZone.Y,
                        bottom: targetZone.Y + targetZone.Height,
                      };
                      return !(
                        left > zoneBounds.right ||
                        right < zoneBounds.left ||
                        top > zoneBounds.bottom ||
                        bottom < zoneBounds.top
                      );
                    });
                  });
                }
              }

              // Fast resync only if: text is equal, non-empty, AND no prep crossouts present
              const exactMatch = s.length > 0 && s === t;
              // If the only difference is edge punctuation, allow fast resync
              const punctuationOnlyDifference = !exactMatch && this.textsDifferOnlyByTrailingPunctuation(s, t);

              if ((exactMatch || punctuationOnlyDifference) && !hasPrepCrossouts) {
                this.startResync(true);
                return;
              }
              this.startResync(false);
            })
            .catch(() => {
              this.startResync(false);
            });
        }
      }
    }
  }

  private applyBlueDifferenceBoxesToSelectedDiffs(
    documentType: DocumentTypes,
    zone: Core.Annotations.Annotation,
  ): void {
    this.clearResyncSelectedFlags(documentType);

    const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
    if (!wrapper) return;

    let annotsInZone = this.getAnnotationsWithinResyncZone(documentType, zone);
    // After optimization (and everywhere after), only include differences whose text highlights are fully inside the zone
    if (zone.getCustomData('optimizedResyncZone') === 'true') {
      annotsInZone = annotsInZone.filter((a) => a.Id && this.isDifferenceFullyInsideZone(documentType, a.Id, zone));
    }

    // Mark differences in the current zone on the specified document
    annotsInZone.forEach((annot) => {
      if (annot.getCustomData('difference') === 'true') {
        annot.setCustomData('resyncSelected', 'true');
        wrapper.instance.Core.annotationManager.redrawAnnotation(annot);
        // Also redraw any paired text highlight so it can reflect resync selection color
        const textHighlight = wrapper.instance.Core.annotationManager.getAnnotationById(`${annot.Id}-text-highlight`);
        if (textHighlight) {
          wrapper.instance.Core.annotationManager.redrawAnnotation(textHighlight);
        }
      }
    });

    // Also redraw any high-contrast overlay annotations that reference these differences
    const focusedId = store.getState().inspection?.focusedDifferenceId;
    if (focusedId) {
      const annotManager = wrapper.instance.Core.annotationManager;
      const overlays = annotManager
        .getAnnotationsList()
        .filter(
          (a: Core.Annotations.Annotation) =>
            a.getCustomData(AnnotationCustomData.highContrast) === 'true' && a.PageNumber === zone.PageNumber,
        );
      overlays.forEach((overlay) => {
        const refIds = (overlay.getCustomData(AnnotationCustomData.refAnnotationIds) || '').split(',');
        if (refIds.includes(focusedId)) {
          annotManager.redrawAnnotation(overlay);
        }
      });
    }
  }

  private clearResyncSelectedFlags(documentType: DocumentTypes): void {
    const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
    if (!wrapper) return;
    const annotManager = wrapper.instance.Core.annotationManager;
    const allAnnotations = annotManager.getAnnotationsList();
    const toClear = allAnnotations.filter(
      (annotation: Core.Annotations.Annotation) => annotation.getCustomData('resyncSelected') === 'true',
    );
    if (toClear.length) {
      toClear.forEach((annot) => annot.setCustomData('resyncSelected', ''));
      // Redraw cleared annotations
      toClear.forEach((annot) => annotManager.redrawAnnotation(annot));

      // Also redraw any paired text highlights to restore their normal color
      toClear.forEach((annot) => {
        const textHighlight = annotManager.getAnnotationById(`${annot.Id}-text-highlight`);
        if (textHighlight) annotManager.redrawAnnotation(textHighlight);
      });

      const focusedId = store.getState().inspection?.focusedDifferenceId;
      if (focusedId) {
        const overlays = allAnnotations.filter(
          (a: Core.Annotations.Annotation) => a.getCustomData(AnnotationCustomData.highContrast) === 'true',
        );
        overlays.forEach((overlay) => {
          const refIds = (overlay.getCustomData(AnnotationCustomData.refAnnotationIds) || '').split(',');
          if (refIds.includes(focusedId)) {
            annotManager.redrawAnnotation(overlay);
          }
        });
      }
    }
  }

  async createCrossoutsFromResyncZones(
    sourceResyncZone: Core.Annotations.Annotation,
    targetResyncZone: Core.Annotations.Annotation,
  ) {
    const crossouts: {
      sourceCrossouts: Array<{ quads: any[]; number: number }>;
      targetCrossouts: Array<{ quads: any[]; number: number }>;
    } = {
      sourceCrossouts: [],
      targetCrossouts: [],
    };

    // Helper function to check if a crossout quad intersects with a resync zone
    const crossoutIntersectsZone = (
      crossout: { quads: any[]; bounds: { x: number; y: number; width: number; height: number } },
      zone: Core.Annotations.Annotation,
    ): boolean => {
      const zoneBounds = {
        left: zone.X,
        right: zone.X + zone.Width,
        top: zone.Y,
        bottom: zone.Y + zone.Height,
      };

      // Check if any quad in the crossout intersects with the zone
      return crossout.quads.some((quad: any) => {
        const { left, right, top, bottom } = getQuadBounds(quad);

        // Check for intersection
        return !(
          left > zoneBounds.right ||
          right < zoneBounds.left ||
          top > zoneBounds.bottom ||
          bottom < zoneBounds.top
        );
      });
    };

    if (this.source) {
      const sourceCrossouts = await this.createCrossoutsForDocument(this.source, sourceResyncZone);
      crossouts.sourceCrossouts = sourceCrossouts.crossouts;

      // Add prep stage crossouts that fall within the source resync zone
      const prepAnnots = this.getUserPrepMarkups(sourceResyncZone.PageNumber, DocumentTypes.source);
      prepAnnots.crossouts.forEach((prepCrossout) => {
        if (crossoutIntersectsZone(prepCrossout, sourceResyncZone)) {
          // Convert to the format expected by backend (1-based numbering to match createCrossoutsForDocument)
          crossouts.sourceCrossouts.push({
            quads: prepCrossout.quads,
            number: crossouts.sourceCrossouts.length + 1,
          });
        }
      });
    }

    if (this.target) {
      const targetCrossouts = await this.createCrossoutsForDocument(this.target, targetResyncZone);
      crossouts.targetCrossouts = targetCrossouts.crossouts;

      // Add prep stage crossouts that fall within the target resync zone
      const prepAnnots = this.getUserPrepMarkups(targetResyncZone.PageNumber, DocumentTypes.target);
      prepAnnots.crossouts.forEach((prepCrossout) => {
        if (crossoutIntersectsZone(prepCrossout, targetResyncZone)) {
          // Convert to the format expected by backend (1-based numbering to match createCrossoutsForDocument)
          crossouts.targetCrossouts.push({
            quads: prepCrossout.quads,
            number: crossouts.targetCrossouts.length + 1,
          });
        }
      });
    }

    return crossouts;
  }

  // Don't delete. This is if we ever need to debug the crossouts.
  private addCrossoutsToDocument(
    wrapper: WebViewerWrapper,
    pageNumber: number,
    crossouts: Array<{ quads: any[]; number: number }>,
  ): void {
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;
    const { instance } = wrapper;
    const annotManager = instance.Core.annotationManager;
    const CrossoutAnnotation = docManager.createCrossoutAnnotation(instance);

    crossouts.forEach((crossout) => {
      const annotation = new CrossoutAnnotation(crossout.number);
      annotation.PageNumber = pageNumber;

      crossout.quads.forEach((quad: any) => {
        annotation.Quads.push(
          new instance.Core.Math.Quad(quad.x1, quad.y1, quad.x2, quad.y2, quad.x3, quad.y3, quad.x4, quad.y4),
        );
      });

      const dims = quadsToDimensions(annotation.Quads);
      annotation.setRect(new instance.Core.Math.Rect(dims.X, dims.Y, dims.X + dims.Width, dims.Y + dims.Height));

      // Style resync crossouts to match resync zone color
      const blue = new instance.Core.Annotations.Color(2, 164, 182);
      (annotation as any).StrokeColor = blue;
      (annotation as any).Color = blue;
      (annotation as any).Opacity = 1;

      annotation.ReadOnly = true;
      annotation.NoResize = true;
      annotation.NoMove = true;
      annotation.IsHoverable = false;
      annotation.setCustomData(AnnotationCustomData.drawMarkups, 'true');
      annotation.setCustomData('resyncCrossout', 'true'); // Mark as resync-specific crossout for cleanup

      annotManager.addAnnotation(annotation);
      annotManager.redrawAnnotation(annotation);
    });
  }

  async createCrossoutsForDocument(
    wrapper: WebViewerWrapper,
    resyncZone: Core.Annotations.Annotation,
  ): Promise<{ crossouts: Array<{ quads: any[]; number: number }> }> {
    const { instance } = wrapper;
    const doc = instance.Core.documentViewer.getDocument();
    const pageNumber = resyncZone.PageNumber;

    try {
      // Threshold for determining if text is "highlighted" - text must be this percentage
      // inside a highlight (both width and height) to avoid being crossed out
      const OVERLAP_THRESHOLD = RESYNC_ZONE_CONFIG.highlightOverlapThreshold; // 95% -> 100% sometimes causes highlighted text to be crossed out

      // Helper function to check if two rectangles intersect
      const rectanglesIntersect = (
        a: { left: number; right: number; top: number; bottom: number },
        b: { left: number; right: number; top: number; bottom: number },
      ) => {
        return !(a.left > b.right || a.right < b.left || a.top > b.bottom || a.bottom < b.top);
      };

      // Helper function to check if text is actually inside a highlight (not just touching)
      const isTextInsideHighlight = (
        textBounds: { left: number; right: number; top: number; bottom: number },
        highlightBounds: { left: number; right: number; top: number; bottom: number },
      ) => {
        // Text must be mostly inside the highlight
        const textWidth = textBounds.right - textBounds.left;
        const textHeight = textBounds.bottom - textBounds.top;

        // Handle degenerate text quads (zero width or height)
        if (textWidth <= 0 || textHeight <= 0) {
          // For degenerate text, check if the point/line is within the highlight bounds
          return (
            textBounds.left >= highlightBounds.left &&
            textBounds.right <= highlightBounds.right &&
            textBounds.top >= highlightBounds.top &&
            textBounds.bottom <= highlightBounds.bottom
          );
        }

        const overlapWidth =
          Math.min(textBounds.right, highlightBounds.right) - Math.max(textBounds.left, highlightBounds.left);
        const overlapHeight =
          Math.min(textBounds.bottom, highlightBounds.bottom) - Math.max(textBounds.top, highlightBounds.top);

        const widthRatio = Math.max(0, overlapWidth) / textWidth;
        const heightRatio = Math.max(0, overlapHeight) / textHeight;

        return widthRatio > OVERLAP_THRESHOLD && heightRatio > OVERLAP_THRESHOLD;
      };

      // Check if there are any difference boxes within the resync zone
      const differenceBoxesInZone = this.getAnnotationsWithinResyncZone(
        wrapper === this.source ? DocumentTypes.source : DocumentTypes.target,
        resyncZone,
      );

      // If no difference boxes found in entire resync zone, include all text within the zone
      if (differenceBoxesInZone.length === 0) {
        const pageText = await doc.loadPageText(pageNumber);
        const textPositions = (await doc.getTextPosition(pageNumber, 0, pageText.length)) as Core.Math.Quad[];

        if (!textPositions || textPositions.length === 0) {
          return { crossouts: [] };
        }

        return { crossouts: [] };
      }

      // 1. Get all text highlights within the resync zone
      const textHighlightAnnots = instance.Core.annotationManager
        .getAnnotationsList()
        .filter((annot: Core.Annotations.Annotation) => {
          if (!annot.Id || !annot.Id.endsWith('-text-highlight')) {
            return false;
          }
          if (annot.PageNumber !== pageNumber) {
            return false;
          }
          const annotBounds = {
            left: annot.X,
            right: annot.X + annot.Width,
            top: annot.Y,
            bottom: annot.Y + annot.Height,
          };
          const resyncZoneBounds = {
            left: resyncZone.X,
            right: resyncZone.X + resyncZone.Width,
            top: resyncZone.Y,
            bottom: resyncZone.Y + resyncZone.Height,
          };
          return rectanglesIntersect(annotBounds, resyncZoneBounds);
        });

      // 2. Extract text highlight quads ONLY from highlights that are inside difference boxes
      const textHighlightQuads: any[] = [];
      textHighlightAnnots.forEach((annot) => {
        // Check if this text highlight is inside any difference box
        const isInsideDifferenceBox = differenceBoxesInZone.some((diffBox) => {
          const highlightBounds = {
            left: annot.X,
            right: annot.X + annot.Width,
            top: annot.Y,
            bottom: annot.Y + annot.Height,
          };
          const diffBoxBounds = {
            left: diffBox.X,
            right: diffBox.X + diffBox.Width,
            top: diffBox.Y,
            bottom: diffBox.Y + diffBox.Height,
          };
          // Check if highlight is completely inside the difference box
          return (
            highlightBounds.left >= diffBoxBounds.left &&
            highlightBounds.right <= diffBoxBounds.right &&
            highlightBounds.top >= diffBoxBounds.top &&
            highlightBounds.bottom <= diffBoxBounds.bottom
          );
        });

        // Only extract quads from highlights that are inside difference boxes
        if (isInsideDifferenceBox) {
          try {
            const textFragmentsData = annot.getCustomData(AnnotationCustomData.textHighlights);
            if (textFragmentsData) {
              const textFragments = JSON.parse(textFragmentsData);
              textFragments.forEach((fragment: any) => {
                const [x, y, width, height] = fragment.rect;
                // Convert to quad format
                const quad = {
                  x1: x,
                  y1: y,
                  x2: x + width,
                  y2: y,
                  x3: x + width,
                  y3: y + height,
                  x4: x,
                  y4: y + height,
                };
                textHighlightQuads.push(quad);
              });
            }
          } catch (error) {
            console.error(
              `Error parsing textFragments data for annotation ID: ${annot.Id}, page: ${annot.PageNumber}`,
              error,
            );
          }
        }
      });

      // 3. Get all text within the resync zone
      const pageText = await doc.loadPageText(pageNumber);
      const textPositions = (await doc.getTextPosition(pageNumber, 0, pageText.length)) as Core.Math.Quad[];

      if (!textPositions || textPositions.length === 0) {
        return { crossouts: [] };
      }

      const allTextRuns = this.groupQuadsIntoTextRuns(textPositions, pageText);
      const resyncZoneTextRuns = allTextRuns.filter((textRun) => this.isTextRunInResyncZone(textRun, resyncZone));

      // 4. For each text run, check if it's inside any text highlight
      const textRunsToStrikeout: any[] = [];

      resyncZoneTextRuns.forEach((textRun) => {
        let isInsideAnyHighlight = false;

        // Check each quad of the text run
        for (const quad of textRun.quads) {
          if (isInsideAnyHighlight) break; // Found a highlight, no need to check more

          const quadBounds = getQuadBounds(quad);

          // Check if this quad intersects with any text highlight quad
          for (const highlightQuad of textHighlightQuads) {
            if (isInsideAnyHighlight) break; // Found a highlight, no need to check more

            const highlightBounds = {
              left: Math.min(highlightQuad.x1, highlightQuad.x2, highlightQuad.x3, highlightQuad.x4),
              right: Math.max(highlightQuad.x1, highlightQuad.x2, highlightQuad.x3, highlightQuad.x4),
              top: Math.min(highlightQuad.y1, highlightQuad.y2, highlightQuad.y3, highlightQuad.y4),
              bottom: Math.max(highlightQuad.y1, highlightQuad.y2, highlightQuad.y3, highlightQuad.y4),
            };

            if (isTextInsideHighlight(quadBounds, highlightBounds)) {
              isInsideAnyHighlight = true;
              break; // Found a highlight, no need to check more
            }
          }
        }

        // If text is not inside any highlight, mark it for crossout
        if (!isInsideAnyHighlight) {
          textRunsToStrikeout.push(textRun);
        }
      });

      // 5. Create crossout objects
      const crossouts: Array<{ quads: any[]; number: number }> = [];
      textRunsToStrikeout.forEach((textRun, index) => {
        crossouts.push({
          quads: textRun.quads,
          number: index + 1,
        });
      });

      return { crossouts };
    } catch (error) {
      console.error('Error creating crossouts:', error);
      return { crossouts: [] };
    }
  }

  groupQuadsIntoTextRuns(
    textPositions: Core.Math.Quad[],
    pageText: string,
  ): Array<{ quads: Core.Math.Quad[]; text: string }> {
    // Group adjacent quads into meaningful text runs (words/phrases)
    const textRuns: Array<{ quads: Core.Math.Quad[]; text: string }> = [];
    let currentRun: Core.Math.Quad[] = [];
    let currentText = '';

    for (let i = 0; i < textPositions.length; i++) {
      const quad = textPositions[i];
      const char = pageText[i];

      if (char === ' ' || char === '\n' || char === '\t') {
        // End current run on whitespace
        if (currentRun.length > 0) {
          textRuns.push({
            quads: [...currentRun],
            text: currentText.trim(),
          });
          currentRun = [];
          currentText = '';
        }
      } else {
        currentRun.push(quad);
        currentText += char;
      }
    }

    // Add final run if exists
    if (currentRun.length > 0) {
      textRuns.push({
        quads: [...currentRun],
        text: currentText.trim(),
      });
    }

    return textRuns.filter((run) => run.text.length > 0);
  }

  isTextRunInResyncZone(
    textRun: { quads: Core.Math.Quad[]; text: string },
    resyncZone: Core.Annotations.Annotation,
  ): boolean {
    if (textRun.quads.length === 0) return false;

    // Check if any part of the text run is within the resync zone
    return textRun.quads.some((quad) => {
      const quadBounds = getQuadBounds(quad);

      const resyncZoneBounds = {
        left: resyncZone.X,
        right: resyncZone.X + resyncZone.Width,
        top: resyncZone.Y,
        bottom: resyncZone.Y + resyncZone.Height,
      };

      return (
        quadBounds.left < resyncZoneBounds.right &&
        quadBounds.right > resyncZoneBounds.left &&
        quadBounds.top < resyncZoneBounds.bottom &&
        quadBounds.bottom > resyncZoneBounds.top
      );
    });
  }

  async startResync(fastResync: boolean) {
    // Verify both zones still exist before starting resync
    const sourceZones = this.getResyncZones(DocumentTypes.source);
    const targetZones = this.getResyncZones(DocumentTypes.target);
    if (sourceZones.length === 0 || targetZones.length === 0) {
      return; // One zone was rejected/deleted, don't start resync
    }

    // Close popups on both documents
    [this.source, this.target].forEach((wrapper) => {
      if (!wrapper) return;
      const inst = wrapper.instance;
      inst.UI.annotationPopup.update([]);
    });

    // Dismiss any snack messages (to prevent Undo option from previous resync sticking around)
    store.dispatch(app.actions.clearSnackMessageType('info'));

    // Clear any document highlight when resync starts
    this.source?.removeAnnotationDocumentHighlight();
    this.target?.removeAnnotationDocumentHighlight();

    // Temporarily lock zoom at current levels to prevent zoom changes during unfocus
    const zoomStore = useZoomStateStore.getState();
    const currentZoomLock = zoomStore.zoomLockLevel;
    const sourceAlreadyLocked = currentZoomLock[DocumentTypes.source] !== null;
    const targetAlreadyLocked = currentZoomLock[DocumentTypes.target] !== null;
    if (!sourceAlreadyLocked) {
      const sourceZoom = this.source?.instance.UI.getZoomLevel() || 100;
      zoomStore.setZoomLockLevel(DocumentTypes.source, sourceZoom);
    }
    if (!targetAlreadyLocked) {
      const targetZoom = this.target?.instance.UI.getZoomLevel() || 100;
      zoomStore.setZoomLockLevel(DocumentTypes.target, targetZoom);
    }

    // Unfocus difference (this triggers zoom changes but zoom is now locked)
    store.dispatch(inspection.actions.unfocusDifference());
    store.dispatch(app.actions.setFocusedMatchIndex(-1));

    // Unlock zoom after a short delay, if we were the ones who locked it
    setTimeout(() => {
      if (!sourceAlreadyLocked) {
        zoomStore.setZoomLockLevel(DocumentTypes.source, null);
      }
      if (!targetAlreadyLocked) {
        zoomStore.setZoomLockLevel(DocumentTypes.target, null);
      }
    }, 100);

    // Get resync zones from both documents
    const sourceResyncZones = this.getResyncZones(DocumentTypes.source);
    const targetResyncZones = this.getResyncZones(DocumentTypes.target);

    if (sourceResyncZones.length === 0 || targetResyncZones.length === 0) {
      console.error('Error: Missing resync zones');
      store.dispatch(inspection.actions.setResyncLoading(false));
      return;
    }
    const sourceAnnotationsInResyncZone = this.getAnnotationsWithinResyncZone(
      DocumentTypes.source,
      sourceResyncZones[0],
    );
    const targetAnnotationsInResyncZone = this.getAnnotationsWithinResyncZone(
      DocumentTypes.target,
      targetResyncZones[0],
    );

    const inspectionId = getInspectionId(store.getState());
    const currentResyncCount = store.getState().inspection.settings.resyncCount;
    const annotationToZone = (annotation: Core.Annotations.Annotation) => ({
      pageNumber: annotation.PageNumber,
      x1: annotation.X,
      x2: annotation.X + annotation.Width,
      y1: annotation.Y,
      y2: annotation.Y + annotation.Height,
    });

    const allDifferences = getDifferences(store.getState());
    const { differenceTypeFilters } = getDifferenceViewOptions(store.getState());

    const sourceIds = new Set(sourceAnnotationsInResyncZone.map((a) => a.Id));
    const targetIds = new Set(targetAnnotationsInResyncZone.map((a) => a.Id));

    // Identify unmatched IDs and their documents
    const sourceOnlyIds = [...sourceIds].filter((id) => !targetIds.has(id));
    const targetOnlyIds = [...targetIds].filter((id) => !sourceIds.has(id));

    // sourceOnlyIds = differences in source resync zone but NOT in target resync zone
    // This means TARGET is missing these differences (target orphaned) → should be INSERTIONS
    const targetOrphanedDifferences = sourceOnlyIds
      .map((id) => allDifferences.find((d) => d.id === id))
      .filter(
        (diff): diff is Difference => diff !== undefined && diff.type !== 'Insertion' && diff.type !== 'Deletion',
      );

    // targetOnlyIds = differences in target resync zone but NOT in source resync zone
    // This means SOURCE is missing these differences (source orphaned) → should be DELETIONS
    const sourceOrphanedDifferences = targetOnlyIds
      .map((id) => allDifferences.find((d) => d.id === id))
      .filter(
        (diff): diff is Difference => diff !== undefined && diff.type !== 'Insertion' && diff.type !== 'Deletion',
      );

    const orphanedDiffs = {
      targetOrphaned: targetOrphanedDifferences, // Need to add as insertions
      sourceOrphaned: sourceOrphanedDifferences, // Need to add as deletions
    };

    const allResyncZoneIds = new Set([...sourceIds, ...targetIds]);

    const differencesToResync = allDifferences.filter(
      (diff) =>
        RESYNC_ALLOWED_TYPES.has(diff.type) && allResyncZoneIds.has(diff.id) && !differenceTypeFilters[diff.type],
    );

    if (inspectionId && sourceResyncZones.length > 0 && targetResyncZones.length > 0) {
      if (fastResync) {
        // Fast path: included text already verified equal → just exclude originals and refresh
        const differenceIds = differencesToResync.map((d) => d.id);
        const breakdown: Record<string, number> = {};
        differencesToResync.forEach((d) => {
          if (differenceIds.includes(d.id)) {
            breakdown[d.type] = (breakdown[d.type] || 0) + 1;
          }
        });

        store.dispatch(
          fastResyncExcludeDifferences({
            inspectionId,
            differenceIds,
            resyncCount: currentResyncCount as number,
            orphanedDiffs,
            sourceResyncZone: annotationToZone(sourceResyncZones[0]),
            targetResyncZone: annotationToZone(targetResyncZones[0]),
            eliminatedTypeBreakdown: breakdown,
          }),
        );
      } else {
        // Full resync path
        const crossouts = await this.createCrossoutsFromResyncZones(sourceResyncZones[0], targetResyncZones[0]);

        store.dispatch(
          resyncAnnotations(inspectionId, {
            sourceResyncZone: annotationToZone(sourceResyncZones[0]),
            targetResyncZone: annotationToZone(targetResyncZones[0]),
            differencesToResync,
            resyncCount: currentResyncCount as number,
            orphanedDiffs,
            crossouts: {
              sourceCrossouts: crossouts.sourceCrossouts,
              targetCrossouts: crossouts.targetCrossouts,
            },
          }),
        );
      }
    }
    return;
  }

  // IF there are difference boxes in the zone, extract the text from the difference boxes
  private async extractIncludedTextFromDifferenceBoxWithinResyncZone(
    wrapper: WebViewerWrapper,
    differenceBoxesInZone: Core.Annotations.Annotation[],
  ): Promise<string> {
    // Gather difference boxes inside the zone
    const documentType = wrapper === this.source ? DocumentTypes.source : DocumentTypes.target;

    // Prefer the Difference text field via Redux
    if (differenceBoxesInZone.length > 0) {
      const diffs = getDifferences(store.getState());
      const diffById = new Map(diffs.map((d) => [d.id, d]));

      const sortedBoxes = [...differenceBoxesInZone].sort((a, b) => {
        if (a.PageNumber !== b.PageNumber) return a.PageNumber - b.PageNumber;
        if (a.Y !== b.Y) return a.Y - b.Y;
        return a.X - b.X;
      });

      const parts: string[] = [];
      sortedBoxes.forEach((annot) => {
        const diff = diffById.get(annot.Id);
        if (!diff) return;
        const text = (diff as any)?.[documentType]?.text as string | undefined;
        if (text && text.trim().length > 0) parts.push(text.trim());
      });

      const aggregated = parts.join(' ');
      if (aggregated.trim().length > 0) return aggregated.trim();
    }

    // If there are no difference boxes selected, return empty so caller can decide fallback
    return '';
  }

  // If there are no difference boxes in the zone, extract the text from the document
  private async extractIncludedTextFromResyncZone(
    wrapper: WebViewerWrapper,
    zone: Core.Annotations.Annotation,
  ): Promise<string> {
    try {
      const doc = wrapper.instance.Core.documentViewer.getDocument();
      const text = await PDFTronManager.extractText(
        doc,
        zone.PageNumber,
        zone.X,
        zone.Y,
        zone.X + zone.Width,
        zone.Y + zone.Height,
      );
      return text.replace(/\r?\n/g, ' ').trim();
    } catch (error) {
      console.error('Error extracting raw text from specified zone:', error);
      return '';
    }
  }

  private async getIncludedTextForResync(
    wrapper: WebViewerWrapper | null,
    differenceBoxesInZone: Core.Annotations.Annotation[],
    resyncZone: Core.Annotations.Annotation,
  ): Promise<string> {
    if (!wrapper) return '';
    if (differenceBoxesInZone.length > 0) {
      return this.extractIncludedTextFromDifferenceBoxWithinResyncZone(wrapper, differenceBoxesInZone);
    }
    return this.extractIncludedTextFromResyncZone(wrapper, resyncZone);
  }

  private textsDifferOnlyByTrailingPunctuation(sourceText: string, targetText: string): boolean {
    const normalizedSource = sourceText.trim();
    const normalizedTarget = targetText.trim();

    const baseHasMeaningfulText = normalizedSource.length > 0 && normalizedTarget.length > 0;
    if (!baseHasMeaningfulText) {
      return false;
    }

    const matchesExceptForEdgePunctuation = (longer: string, shorter: string) => {
      if (longer.length <= shorter.length || longer.length - shorter.length > 2) {
        return false;
      }

      const hasLeadingPunc = () => /^\p{P}$/u.test(longer.charAt(0));
      const hasTrailingPunc = () => /^\p{P}$/u.test(longer.charAt(longer.length - 1));

      // Leading-only or trailing-only punctuation
      if (longer.length === shorter.length + 1) {
        if (hasLeadingPunc() && longer.slice(1) === shorter) {
          return true;
        }
        if (hasTrailingPunc() && longer.slice(0, -1) === shorter) {
          return true;
        }
        return false;
      }

      // Both edges contain punctuation (e.g., "“TEXT”")
      if (longer.length === shorter.length + 2) {
        return hasLeadingPunc() && hasTrailingPunc() && longer.slice(1, -1) === shorter;
      }

      return false;
    };

    return (
      matchesExceptForEdgePunctuation(normalizedSource, normalizedTarget) ||
      matchesExceptForEdgePunctuation(normalizedTarget, normalizedSource)
    );
  }

  deleteResyncZones(): void {
    // Delete resync zones from both documents
    const sourceResyncZones = this.getResyncZones(DocumentTypes.source);
    const targetResyncZones = this.getResyncZones(DocumentTypes.target);

    // Delete from source document
    if (sourceResyncZones.length > 0 && this.source) {
      this.source.instance.Core.annotationManager.deleteAnnotations(sourceResyncZones, { imported: true, force: true });
    }

    // Delete from target document
    if (targetResyncZones.length > 0 && this.target) {
      this.target.instance.Core.annotationManager.deleteAnnotations(targetResyncZones, { imported: true, force: true });
    }

    // Also remove any resync popup anchor annotations and close popups on both viewers
    [this.source, this.target].forEach((wrapper) => {
      if (!wrapper) return;
      try {
        wrapper.instance.UI.annotationPopup.update([]);
      } catch (_) {}
      try {
        const annotMgr = wrapper.instance.Core.annotationManager;
        const list = annotMgr.getAnnotationsList();
        const toDelete = list.filter(
          (a: Core.Annotations.Annotation) =>
            a.getCustomData?.('resyncPopupAnchor') === 'true' ||
            a.getCustomData?.('resyncBlueTextHighlight') === 'true',
        );
        if (toDelete.length) annotMgr.deleteAnnotations(toDelete, { imported: true, force: true });
      } catch (_) {}
    });

    // Clear any blue highlights when zones are deleted
    this.clearResyncSelectedFlags(DocumentTypes.source);
    this.clearResyncSelectedFlags(DocumentTypes.target);

    // Also restore any differences hidden for resync
    this.restoreHiddenDifferences();
  }

  deleteResyncCrossouts(): void {
    if (this.source) {
      this.deleteResyncCrossoutsFromDocument(this.source);
    }
    if (this.target) {
      this.deleteResyncCrossoutsFromDocument(this.target);
    }
  }

  private deleteResyncCrossoutsFromDocument(wrapper: WebViewerWrapper): void {
    const { instance } = wrapper;
    const annotManager = instance.Core.annotationManager;

    // Get all annotations and filter for resync crossouts
    const allAnnotations = annotManager.getAnnotationsList();
    const resyncCrossouts = allAnnotations.filter(
      (annotation: Core.Annotations.Annotation) => annotation.getCustomData('resyncCrossout') === 'true',
    );

    if (resyncCrossouts.length > 0) {
      annotManager.deleteAnnotations(resyncCrossouts, { imported: true, force: true });
    }
  }

  private getResyncZones(documentType: DocumentTypes): Core.Annotations.Annotation[] {
    const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
    if (!wrapper) return [];

    const annotations = wrapper.getAnnotationList();
    return annotations.filter((annotation) => annotation.getCustomData('resyncZoneAnnotation') === 'true');
  }

  private getAnnotationsWithinResyncZone(
    documentType: DocumentTypes,
    resyncZone: Core.Annotations.Annotation,
  ): Core.Annotations.Annotation[] {
    const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
    if (!wrapper) return [];

    const allAnnotations = wrapper.getAnnotationList();
    const allowedIds = this.getAllowedDifferenceIds();

    // Determine which text highlight annotations (visible, allowed) intersect/touch the resync zone
    const zoneBounds = {
      left: resyncZone.X,
      right: resyncZone.X + resyncZone.Width,
      top: resyncZone.Y,
      bottom: resyncZone.Y + resyncZone.Height,
    };

    const textHighlightsInZoneParentIds = new Set<string>();
    const requireFullContainment = resyncZone.getCustomData('optimizedResyncZone') === 'true';
    allAnnotations.forEach((a: Core.Annotations.Annotation) => {
      if (a.PageNumber !== resyncZone.PageNumber) return;
      const isTextHighlight =
        (a as any).Id?.endsWith?.('-text-highlight') || a.getCustomData('isTextHighlight') === 'true';
      if (!isTextHighlight) return;

      // Visibility checks
      if ((a as any).Hidden === true || (a as any).NoView === true) return;
      if (a.getCustomData('resyncHidden') === 'true') return;

      // Identify parent difference id
      let parentId: string | undefined = a.getCustomData('parentId');
      if (!parentId && (a as any).Id?.endsWith?.('-text-highlight')) {
        parentId = (a as any).Id.replace('-text-highlight', '');
      }
      if (!parentId) return;
      if (!allowedIds.has(parentId)) return; // Only consider allowed difference types

      // Intersection check based on actual text fragments, not the annotation's outer rect
      try {
        const data = a.getCustomData(AnnotationCustomData.textHighlights);
        if (!data) return;
        const fragments = JSON.parse(data);
        if (!Array.isArray(fragments)) return;

        const fragmentIntersects = (frag: any) => {
          const rect = frag?.rect;
          if (!rect || rect.length !== 4) return false;
          const x = rect[0];
          const y = rect[1];
          const w = rect[2];
          const h = rect[3];
          const fBounds = { left: x, right: x + w, top: y, bottom: y + h };
          return !(
            fBounds.left > zoneBounds.right ||
            fBounds.right < zoneBounds.left ||
            fBounds.top > zoneBounds.bottom ||
            fBounds.bottom < zoneBounds.top
          );
        };

        const fragmentInside = (frag: any) => {
          const rect = frag?.rect;
          if (!rect || rect.length !== 4) return false;
          const x = rect[0];
          const y = rect[1];
          const w = rect[2];
          const h = rect[3];
          const left = x;
          const right = x + w;
          const top = y;
          const bottom = y + h;
          return (
            left >= zoneBounds.left && right <= zoneBounds.right && top >= zoneBounds.top && bottom <= zoneBounds.bottom
          );
        };

        const include = requireFullContainment
          ? fragments.length > 0 && fragments.every(fragmentInside)
          : fragments.some(fragmentIntersects);
        if (include) textHighlightsInZoneParentIds.add(parentId);
      } catch (_) {}
    });

    if (textHighlightsInZoneParentIds.size === 0) return [];

    // Return the corresponding difference annotations (rectangles/markers) matching those parent ids
    return allAnnotations.filter((annotation) => {
      if (annotation.PageNumber !== resyncZone.PageNumber) return false;
      if (annotation.getCustomData('difference') !== 'true') return false;
      if (annotation.getCustomData('resyncHidden') === 'true') return false;
      if ((annotation as any).Hidden === true || (annotation as any).NoView === true) return false;
      const idOk = !!annotation.Id && textHighlightsInZoneParentIds.has(annotation.Id);
      if (!idOk) return false;
      return true;
    });
  }

  // Check if ALL text highlight fragments for a difference are fully inside the given zone
  private isDifferenceFullyInsideZone(
    documentType: DocumentTypes,
    parentId: string,
    zone: Core.Annotations.Annotation,
  ): boolean {
    const wrapper = documentType === DocumentTypes.source ? this.source : this.target;
    if (!wrapper) return false;
    const annotManager = wrapper.instance.Core.annotationManager;
    const allAnnots = annotManager.getAnnotationsList();

    // Find text highlight annotation for this difference
    const textHighlight = allAnnots.find((a: Core.Annotations.Annotation) => {
      if (a.PageNumber !== zone.PageNumber) return false;
      const isTH = (a as any).Id?.endsWith?.('-text-highlight') || a.getCustomData('isTextHighlight') === 'true';
      if (!isTH) return false;
      const pid = a.getCustomData('parentId') || (a as any).Id?.replace?.('-text-highlight', '');
      return pid === parentId;
    });
    if (!textHighlight) return false;

    const zoneBounds = {
      left: zone.X,
      right: zone.X + zone.Width,
      top: zone.Y,
      bottom: zone.Y + zone.Height,
    };

    try {
      const data = textHighlight.getCustomData(AnnotationCustomData.textHighlights);
      if (!data) return false;
      const fragments = JSON.parse(data);
      if (!Array.isArray(fragments) || fragments.length === 0) return false;
      return fragments.every((frag: any) => {
        const rect = frag?.rect;
        if (!rect || rect.length !== 4) return false;
        const x = rect[0];
        const y = rect[1];
        const w = rect[2];
        const h = rect[3];
        const left = x;
        const right = x + w;
        const top = y;
        const bottom = y + h;
        return (
          left >= zoneBounds.left && right <= zoneBounds.right && top >= zoneBounds.top && bottom <= zoneBounds.bottom
        );
      });
    } catch (_) {
      return false;
    }
  }

  // Compute allowed difference IDs based on RESYNC_ALLOWED_TYPES
  private getAllowedDifferenceIds(): Set<string> {
    const diffs = getDifferences(store.getState());
    const allowedIds = new Set<string>();
    diffs.forEach((d) => {
      if (RESYNC_ALLOWED_TYPES.has(d.type)) {
        // Exclude IDs that have been tagged disabled in the annotations (defensive, most calls go through annotations)
        allowedIds.add(d.id);
      }
    });
    return allowedIds;
  }

  // Hide all non-allowed difference-related annotations while resync tool is active
  private hideDisallowedDifferences(): void {
    const allowedIds = this.getAllowedDifferenceIds();
    const { filtered } = getFilteredAndUnfilteredDifferences(store.getState());
    const filteredIds = new Set(filtered.map((d) => d.id));

    [this.source, this.target].forEach((wrapper) => {
      if (!wrapper) return;
      const { instance } = wrapper;
      const annotManager = instance.Core.annotationManager;
      const allAnnots = annotManager.getAnnotationsList();

      const toHide: Core.Annotations.Annotation[] = [];
      allAnnots.forEach((annot: Core.Annotations.Annotation) => {
        const isDifference = annot.getCustomData(AnnotationCustomData.difference) === 'true';
        const isTextHighlight = annot.getCustomData('isTextHighlight') === 'true';
        if (!isDifference && !isTextHighlight) return;

        // Track current visibility state; for text highlights we still want to tag even if hidden
        const alreadyHidden = (annot as any).Hidden === true || (annot as any).NoView === true;

        let isAllowed = false;
        if (isTextHighlight) {
          const type = annot.getCustomData('differenceType') as Difference['type'];
          isAllowed = !!type && RESYNC_ALLOWED_TYPES.has(type);
        } else {
          // For difference rectangles and deletion markers, use the annotation Id
          const diffId = annot.Id;
          isAllowed = !!diffId && allowedIds.has(diffId);
        }

        // Do not tag annotations that are already filtered out by differenceTypeFilters
        const parentId = isTextHighlight ? annot.getCustomData('parentId') : annot.Id;
        const isFilteredOutByType = !!parentId && filteredIds.has(parentId);

        if (!isAllowed && !isFilteredOutByType && annot.getCustomData('resyncHidden') !== 'true') {
          // Always tag so future toggles (e.g., turning text highlights ON) won't show disallowed ones
          annot.setCustomData('resyncHidden', 'true');
          // Only push to hide if the annotation is currently shown; if it's already hidden, skip to avoid flicker
          if (!alreadyHidden) {
            toHide.push(annot);
          }
        }
      });
      if (toHide.length > 0) {
        annotManager.hideAnnotations(toHide);
      }
    });
  }

  // Restore any annotations hidden during resync tool activity
  private restoreHiddenDifferences(): void {
    if (!isResultUrl()) return;
    [this.source, this.target].forEach((wrapper) => {
      if (!wrapper) return;
      const { instance } = wrapper;
      const annotManager = instance.Core.annotationManager;
      const allAnnots = annotManager.getAnnotationsList();
      const toShow = allAnnots.filter(
        (annot: Core.Annotations.Annotation) => annot.getCustomData('resyncHidden') === 'true',
      );

      if (toShow.length > 0) {
        toShow.forEach((annot) => annot.setCustomData('resyncHidden', ''));
        annotManager.showAnnotations(toShow);
      }
    });
  }
}

// Static utility function that can be called from actions
export const cleanupResyncZonesAndResetTool = (): void => {
  const pdfManager = PDFManagerFactory.getPDFDocManager();
  if (pdfManager) {
    pdfManager.deleteResyncZones();
    pdfManager.deleteResyncCrossouts();
    // Also remove any resync-zone text highlight annotations created during preview
    [DocumentTypes.source, DocumentTypes.target].forEach((docType) => {
      try {
        const viewer = PDFManagerFactory.getViewer(docType);
        if (!viewer) return;
        const annotManager = viewer.instance.Core.annotationManager;
        const toDelete = annotManager
          .getAnnotationsList()
          .filter((a: Core.Annotations.Annotation) => a.getCustomData?.('resyncBlueTextHighlight') === 'true');
        if (toDelete.length) {
          annotManager.deleteAnnotations(toDelete, { imported: true, force: true });
        }
      } catch (_) {
        // ignore
      }
    });
    pdfManager.toolsMixin.resetPDFTronTool();
    store.dispatch(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
  }
};

export default ResyncZone;
